import * as React from 'react';
import { useMemo, useEffect } from 'react';
import classNames from 'classnames';
import FieldForm, { List } from 'rc-field-form';
import { FormProps as RcFormProps } from 'rc-field-form/lib/Form';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { ColProps } from 'antd/lib/col';
import { ConfigContext } from 'antd/lib/config-provider/context';
import { FormContext, FormContextProps } from './context';
import { FormLabelAlign } from './interface';
import { cascadeHandler } from './util'
import useForm, { FormInstance } from '../hooks/useForm';
import SizeContext, { SizeType, SizeContextProvider } from '../../config-provider/SizeContext';
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver';
import defaultLocale from '../../locale/default';

export type RequiredMark = boolean | 'optional';
export type FormLayout = 'horizontal' | 'inline' | 'vertical';

export interface FormProps<Values = any> extends Omit<RcFormProps<Values>, 'form'> {
  prefixCls?: string;
  colon?: boolean;
  name?: string;
  layout?: FormLayout;
  labelAlign?: FormLabelAlign;
  dataBinding?: boolean;
  labelCol?: ColProps;
  wrapperCol?: ColProps;
  form?: FormInstance<Values>;
  size?: SizeType;
  scrollToFirstError?: boolean;
  requiredMark?: RequiredMark;
  /** @deprecated Will warning in future branch. Pls use `requiredMark` instead. */
  hideRequiredMark?: boolean;
}

export interface SimpleFormDependencyLocale {
  requestFail: string;
}

// @ts-ignore
const InternalForm: React.ForwardRefRenderFunction<unknown, FormProps> = (props, ref) => {
  const contextSize = React.useContext(SizeContext);
  // @ts-ignore
  const { getPrefixCls, direction, form: contextForm } = React.useContext(ConfigContext);

  const { name } = props;

  const {
    prefixCls: customizePrefixCls,
    className = '',
    size = contextSize,
    form,
    dataBinding = false,
    colon,
    labelAlign,
    labelCol,
    wrapperCol,
    hideRequiredMark,
    layout = 'horizontal',
    scrollToFirstError,
    requiredMark,
    onFinishFailed,
    onValuesChange,
    ...restFormProps
  } = props;

  const mergedRequiredMark = useMemo(() => {
    if (requiredMark !== undefined) {
      return requiredMark;
    }

    if (contextForm && contextForm.requiredMark !== undefined) {
      return contextForm.requiredMark;
    }

    if (hideRequiredMark) {
      return false;
    }

    return true;
  }, [hideRequiredMark, requiredMark, contextForm]);

  const prefixCls = getPrefixCls('form', customizePrefixCls);
  const simplePrefixCls = getPrefixCls('simple-form', customizePrefixCls);

  const formClassName = classNames(
    prefixCls,
    simplePrefixCls,
    {
      [`${prefixCls}-${layout}`]: true,
      [`${prefixCls}-hide-required-mark`]: mergedRequiredMark === false,
      [`${prefixCls}-rtl`]: direction === 'rtl',
      [`${prefixCls}-${size}`]: size,
    },
    className,
  );

  const [wrapForm] = useForm(form);
  const { __INTERNAL__, setFieldValue } = wrapForm;
  const {allDependency, changeHandler, itemRef} = __INTERNAL__;
  __INTERNAL__.name = name;

  const formContextValue = useMemo<FormContextProps>(
    () => ({
      name,
      allDependency,
      changeHandler,
      setFieldValue,
      dataBinding,
      labelAlign,
      labelCol,
      wrapperCol,
      vertical: layout === 'vertical',
      colon,
      requiredMark: mergedRequiredMark,
      itemRef,
    }),
    [name, allDependency, changeHandler, setFieldValue, dataBinding, labelAlign, labelCol, wrapperCol, layout, colon, mergedRequiredMark],
  );

  React.useImperativeHandle(ref, () => wrapForm);

  const onInternalFinishFailed = (errorInfo: ValidateErrorEntity) => {
    if (onFinishFailed) {
      onFinishFailed(errorInfo);
    }

    if (scrollToFirstError && errorInfo.errorFields.length) {
      wrapForm.scrollToField(errorInfo.errorFields[0].name);
    }
  };

  const onInternalValuesChange = (changedValues: any, allValues: any) => {
    if (onValuesChange) {
      onValuesChange(changedValues, allValues);
    }

    if (Object.keys(allDependency).length) {
      let currentItem = Object.keys(changedValues)[0];
      cascadeHandler(wrapForm, allValues, allDependency, currentItem);
    }
  }

  useEffect(() => {
    if (restFormProps.initialValues && Object.keys(allDependency).length) {
      const allValues = wrapForm.getFieldsValue();
      cascadeHandler(wrapForm, allValues, allDependency);
    }
  }, [])

  return (
    <LocaleReceiver componentName="Form" defaultLocale={defaultLocale.Form}>
      {(locale: SimpleFormDependencyLocale) => {
        __INTERNAL__.locale = locale;
        return (
          <SizeContextProvider size={size}>
            <FormContext.Provider value={formContextValue}>
              <FieldForm
                id={name}
                {...restFormProps}
                onFinishFailed={onInternalFinishFailed}
                onValuesChange={onInternalValuesChange}
                form={wrapForm}
                className={formClassName}
              />
            </FormContext.Provider>
          </SizeContextProvider>
        )
      }}
    </LocaleReceiver>
  );
};

const Form = React.forwardRef<FormInstance, FormProps>(InternalForm) as <Values = any>(
  props: React.PropsWithChildren<FormProps<Values>> & { ref?: React.Ref<FormInstance<Values>> },
) => React.ReactElement;

export { useForm, List, FormInstance };

export default Form;



// WEBPACK FOOTER //
// ./src/components/simple-form/utils/Form.tsx